import React, { useRef, useState, useCallback, useEffect } from "react";
import { useEditor } from "@/lib/store";
import { objectFootprint, OBJECT_DEFS, findNarrowAisles, detectRackRowAisles } from "@/lib/objectDefs";
import { DOOR_KINDS } from "./PropertiesPanel";

// Coordinate conventions:
//   Floor coords are in millimetres. X right, Y forward (top-down view).
//   We pan/zoom and convert to screen pixels. 1 mm * scale = px.

export default function Canvas2D() {
  const svgRef = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [view, setView] = useState({ scale: 0.04, tx: 80, ty: 80 }); // px per mm + offsets
  const [drag, setDrag] = useState(null); // { kind:'pan'|'object'|'obstacle', id?, startX, startY, origX, origY }
  const [snapHints, setSnapHints] = useState(null); // { vLines:[x…], hLines:[y…] } — visual guide lines in mm

  const floor = useEditor((s) => s.floor);
  const objects = useEditor((s) => s.objects);
  const selectedId = useEditor((s) => s.selectedId);
  const setSelected = useEditor((s) => s.setSelected);
  const updateObject = useEditor((s) => s.updateObject);
  const updateObstacle = useEditor((s) => s.updateObstacle);
  const addVertex = useEditor((s) => s.addVertex);
  const setVertex = useEditor((s) => s.setVertex);
  const snap = useEditor((s) => s.snap);
  const smartSnap = useEditor((s) => s.smartSnap);
  const aisleGap = useEditor((s) => s.aisleGap);
  const compliance = useEditor((s) => s.compliance);
  const showAisles = useEditor((s) => s.showAisles);
  const showGrid = useEditor((s) => s.showGrid);

  // Memoise the aisle-row detection so the O(n²) scan doesn't re-run on
  // every pointer move (only when objects or the toggle change).
  const rowAisles = React.useMemo(
    () => (showAisles ? detectRackRowAisles(objects, 6000) : []),
    [showAisles, objects]
  );
  const showDimensions = useEditor((s) => s.showDimensions);
  const measureMode = useEditor((s) => s.measureMode);
  const measure = useEditor((s) => s.measure);
  const addMeasurePoint = useEditor((s) => s.addMeasurePoint);
  const cursorMm = useEditor((s) => s.cursorMm);
  const setCursorMm = useEditor((s) => s.setCursorMm);
  const clearMeasure = useEditor((s) => s.clearMeasure);
  const toggleMeasure = useEditor((s) => s.toggleMeasure);
  const handMode = useEditor((s) => s.handMode);
  const toggleHand = useEditor((s) => s.toggleHand);
  const fitVersion = useEditor((s) => s.fitVersion);
  const fitView = useEditor((s) => s.fitView);
  const removeObject = useEditor((s) => s.removeObject);
  const removeObstacle = useEditor((s) => s.removeObstacle);
  const updateDoor = useEditor((s) => s.updateDoor);
  const undo = useEditor((s) => s.undo);
  const redo = useEditor((s) => s.redo);

  // ResizeObserver to track svg size
  useEffect(() => {
    if (!svgRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      setSize({ w: e.contentRect.width, h: e.contentRect.height });
    });
    ro.observe(svgRef.current);
    return () => ro.disconnect();
  }, []);

  // Frame the floor within the SVG. Called on shape change AND on manual fit.
  const fit = useCallback(() => {
    if (!floor.vertices || floor.vertices.length < 2) return;
    const xs = floor.vertices.map((v) => v.x);
    const ys = floor.vertices.map((v) => v.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const w = Math.max(1000, maxX - minX);
    const h = Math.max(1000, maxY - minY);
    const pad = 80;
    const sx = (size.w - pad * 2) / w;
    const sy = (size.h - pad * 2) / h;
    const scale = Math.min(sx, sy);
    if (!Number.isFinite(scale) || scale <= 0) return;
    setView({ scale, tx: pad - minX * scale, ty: pad - minY * scale });
  }, [floor.vertices, size.w, size.h]);

  // Auto-fit when the shape changes or the container resizes.
  useEffect(() => { fit(); }, [floor.shape, size.w, size.h, fit]);

  // Manual fit triggered from toolbar / F key.
  useEffect(() => {
    if (fitVersion > 0) fit();
  }, [fitVersion, fit]);

  const toScreen = useCallback((x, y) => ({
    sx: x * view.scale + view.tx,
    sy: y * view.scale + view.ty,
  }), [view]);

  const toWorld = useCallback((sx, sy) => ({
    x: (sx - view.tx) / view.scale,
    y: (sy - view.ty) / view.scale,
  }), [view]);

  const snapMm = (v) => Math.round(v / snap) * snap;

  /**
   * Smart-snap during drag:
   * Aligns the moving object's bounding-box edges flush with either the closest
   * warehouse wall or another object's edge. Returns the corrected top-left
   * position AND the set of guide lines (in floor mm) to render as feedback.
   *
   * Only considers axis-aligned walls (dx=0 or dy=0) — sufficient for
   * rectangular, L-shape and orthogonal polygon floors we currently support.
   */
  function snapToBoundsAndNeighbours(desiredX, desiredY, movingObj) {
    const fp = objectFootprint(movingObj);
    const L = fp.length, W = fp.width;

    // Candidate X and Y positions. Each candidate carries an optional `gap`
    // used to render the live "aisle gap" readout on the canvas.
    const candX = []; // {val, gap}
    const candY = [];

    const verts = floor.vertices || [];
    // Walls — polygon edge is the interior wall face (walls sit outside).
    // For each axis-aligned wall, emit two flush candidates AND (if aisleGap>0)
    // two aisle-gap candidates positioned `aisleGap` mm INSIDE the wall.
    for (let i = 0; i < verts.length; i++) {
      const a = verts[i];
      const b = verts[(i + 1) % verts.length];
      if (Math.abs(a.x - b.x) < 1) {
        // Vertical wall at x = a.x. Interior of polygon is either +x or -x
        // side of the wall; we test with the polygon centroid.
        const cx = verts.reduce((s, v) => s + v.x, 0) / verts.length;
        const sign = cx > a.x ? +1 : -1; // interior direction from wall
        // Flush candidates
        candX.push({ val: a.x,     gap: 0 });         // leaning against with left edge
        candX.push({ val: a.x - L, gap: 0 });         // leaning with right edge on the wall
        if (aisleGap > 0) {
          candX.push({ val: a.x + sign * aisleGap,     gap: aisleGap }); // aisle from wall
          candX.push({ val: a.x + sign * aisleGap - L, gap: aisleGap });
        }
      } else if (Math.abs(a.y - b.y) < 1) {
        const cy = verts.reduce((s, v) => s + v.y, 0) / verts.length;
        const sign = cy > a.y ? +1 : -1;
        candY.push({ val: a.y,     gap: 0 });
        candY.push({ val: a.y - W, gap: 0 });
        if (aisleGap > 0) {
          candY.push({ val: a.y + sign * aisleGap,     gap: aisleGap });
          candY.push({ val: a.y + sign * aisleGap - W, gap: aisleGap });
        }
      }
    }
    // Other objects — flush + aisle-gap on both sides.
    for (const o of objects) {
      if (o.id === movingObj.id) continue;
      const ofp = objectFootprint(o);
      const l = o.position.x, r = o.position.x + ofp.length;
      const t = o.position.y, bt = o.position.y + ofp.width;
      // Touch: moving.left = neighbour.right (right of neighbour)
      candX.push({ val: r, gap: 0 });         // flush on left of moving
      candX.push({ val: l - L, gap: 0 });     // flush on right of moving
      candX.push({ val: l, gap: 0 });         // left-aligned rows
      candX.push({ val: r - L, gap: 0 });     // right-aligned rows
      if (aisleGap > 0) {
        candX.push({ val: r + aisleGap, gap: aisleGap });
        candX.push({ val: l - aisleGap - L, gap: aisleGap });
      }
      candY.push({ val: bt, gap: 0 });
      candY.push({ val: t - W, gap: 0 });
      candY.push({ val: t, gap: 0 });
      candY.push({ val: bt - W, gap: 0 });
      if (aisleGap > 0) {
        candY.push({ val: bt + aisleGap, gap: aisleGap });
        candY.push({ val: t - aisleGap - W, gap: aisleGap });
      }
    }

    // Threshold — a fixed screen distance (12 px) converted to mm at current zoom.
    const thresholdMm = 12 / view.scale;

    let bestX = null, bestY = null;
    let bestDx = thresholdMm, bestDy = thresholdMm;
    let guideX = null, guideY = null;
    let gapX = 0, gapY = 0;
    for (const c of candX) {
      const d = Math.abs(desiredX - c.val);
      if (d < bestDx) { bestDx = d; bestX = c.val; guideX = c.val; gapX = c.gap; }
    }
    for (const c of candY) {
      const d = Math.abs(desiredY - c.val);
      if (d < bestDy) { bestDy = d; bestY = c.val; guideY = c.val; gapY = c.gap; }
    }

    const finalX = bestX !== null ? bestX : desiredX;
    const finalY = bestY !== null ? bestY : desiredY;
    const vLines = guideX !== null ? [guideX, guideX === finalX ? finalX + L : finalX] : [];
    const hLines = guideY !== null ? [guideY, guideY === finalY ? finalY + W : finalY] : [];
    return { x: finalX, y: finalY, vLines, hLines, gapX, gapY };
  }

  // Handlers
  function onWheel(e) {
    e.preventDefault();
    const rect = svgRef.current.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const w = toWorld(sx, sy);
    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(0.005, Math.min(0.5, view.scale * factor));
    setView({
      scale: newScale,
      tx: sx - w.x * newScale,
      ty: sy - w.y * newScale,
    });
  }

  function onPointerDown(e) {
    const rect = svgRef.current.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const world = toWorld(sx, sy);

    // HAND TOOL — left drag always pans; everything else is ignored.
    if (handMode && e.button === 0) {
      e.preventDefault();
      setDrag({ kind: "pan", startX: sx, startY: sy, origTx: view.tx, origTy: view.ty });
      return;
    }

    // MEASURE MODE — clicks record measurement points
    if (measureMode && e.button === 0) {
      addMeasurePoint({ x: snapMm(world.x), y: snapMm(world.y) });
      return;
    }

    // polygon point insertion mode
    if (floor.shape === "polygon" && e.button === 0 && e.target === svgRef.current) {
      addVertex({ x: snapMm(world.x), y: snapMm(world.y) });
      return;
    }

    if (e.button === 1 || e.button === 2 || (e.button === 0 && e.target === svgRef.current)) {
      e.preventDefault();
      setDrag({ kind: "pan", startX: sx, startY: sy, origTx: view.tx, origTy: view.ty });
    }
  }

  function onPointerMove(e) {
    const rect = svgRef.current.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    // Always track cursor position in mm — used by measure preview / status.
    const world = toWorld(sx, sy);
    setCursorMm({ x: snapMm(world.x), y: snapMm(world.y) });

    if (!drag) return;

    if (drag.kind === "pan") {
      setView((v) => ({ ...v, tx: drag.origTx + (sx - drag.startX), ty: drag.origTy + (sy - drag.startY) }));
    } else if (drag.kind === "object") {
      const dxMm = (sx - drag.startX) / view.scale;
      const dyMm = (sy - drag.startY) / view.scale;
      // Base position after grid-snap
      let nx = snapMm(drag.origX + dxMm);
      let ny = snapMm(drag.origY + dyMm);
      // Smart-snap: align edges flush with walls / other objects.
      if (smartSnap) {
        const movingObj = objects.find((o) => o.id === drag.id);
        if (movingObj) {
          const s = snapToBoundsAndNeighbours(nx, ny, movingObj);
          nx = s.x; ny = s.y;
          setSnapHints((s.vLines.length || s.hLines.length)
            ? { vLines: s.vLines, hLines: s.hLines, gapX: s.gapX, gapY: s.gapY }
            : null);
        }
      } else {
        setSnapHints(null);
      }
      updateObject(drag.id, { position: { x: nx, y: ny } });
    } else if (drag.kind === "obstacle") {
      const dxMm = (sx - drag.startX) / view.scale;
      const dyMm = (sy - drag.startY) / view.scale;
      updateObstacle(drag.id, {
        x: snapMm(drag.origX + dxMm),
        y: snapMm(drag.origY + dyMm),
      });
    } else if (drag.kind === "door") {
      // Project the pointer delta onto the wall's tangent. Doors are constrained
      // to slide along their own wall; new offset is clamped so the door never
      // overlaps the wall endpoints.
      const dxScreen = sx - drag.startX;
      const dyScreen = sy - drag.startY;
      const tangentPx = dxScreen * drag.ux + dyScreen * drag.uy; // signed pixels
      const deltaMm = tangentPx / view.scale;
      const maxOff = Math.max(0, drag.wallLen - drag.doorWidth);
      const next = Math.max(0, Math.min(maxOff, snapMm(drag.origOffset + deltaMm)));
      updateDoor(drag.id, { offset: next });
    } else if (drag.kind === "vertex") {
      const w = toWorld(sx, sy);
      setVertex(drag.idx, { x: snapMm(w.x), y: snapMm(w.y) });
    }
  }

  function onPointerUp() { setDrag(null); setSnapHints(null); }

  // Keyboard shortcuts:
  //   Esc     — exit measure / hand / clear tape draft
  //   Space   — deselect: clear any active tool (hand, measure) AND selected object
  //   Delete  — remove the currently selected object
  //   H       — toggle hand tool  (Photoshop-style)
  //   M       — toggle measuring tape
  //   F       — fit view
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      const inField = tag === "INPUT" || tag === "TEXTAREA" || (e.target && e.target.isContentEditable);
      if (e.key === "Escape") {
        if (measureMode) {
          if (measure) clearMeasure();
          else toggleMeasure();
        } else if (handMode) {
          toggleHand();
        } else if (selectedId) {
          setSelected(null);
        }
        return;
      }
      // Cmd/Ctrl + Z — undo, Cmd/Ctrl + Shift + Z (or Ctrl + Y) — redo.
      // Handled BEFORE the `inField` guard so a focused input doesn't swallow
      // the shortcut.
      if ((e.metaKey || e.ctrlKey) && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === "y" || e.key === "Y")) {
        e.preventDefault();
        redo();
        return;
      }
      if (inField || e.metaKey || e.ctrlKey || e.altKey) return;
      // Space — universal "unselect" shortcut. Cancels the active tool, drops
      // any object selection, and prevents the default page-scroll behaviour.
      if (e.key === " " || e.code === "Space") {
        e.preventDefault();
        if (measureMode) { clearMeasure(); toggleMeasure(); }
        if (handMode) toggleHand();
        if (selectedId) setSelected(null);
        return;
      }
      // Delete / Backspace — remove currently selected object (racks, MHE, etc.)
      if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedId) {
          e.preventDefault();
          if (selectedId.startsWith("ob_")) removeObstacle(selectedId);
          else removeObject(selectedId);
          setSelected(null);
        }
        return;
      }
      if (e.key === "h" || e.key === "H") { e.preventDefault(); toggleHand(); return; }
      if (e.key === "m" || e.key === "M") { e.preventDefault(); toggleMeasure(); return; }
      if (e.key === "f" || e.key === "F") { e.preventDefault(); fitView(); return; }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [measureMode, measure, clearMeasure, toggleMeasure, handMode, toggleHand, fitView, selectedId, setSelected, removeObject, removeObstacle, undo, redo]);

  // ----- render -----
  // Floor polygon points string
  const polyPoints = (floor.vertices || []).map((v) => {
    const s = toScreen(v.x, v.y);
    return `${s.sx},${s.sy}`;
  }).join(" ");

  const verts = floor.vertices || [];

  return (
    <div className={`relative w-full h-full ${showGrid ? "grid-bg-fine" : "bg-white"} overflow-hidden`}>
      <svg
        ref={svgRef}
        className="absolute inset-0 w-full h-full select-none"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onContextMenu={(e) => e.preventDefault()}
        style={{ cursor: drag ? "grabbing" : handMode ? "grab" : measureMode ? "crosshair" : floor.shape === "polygon" ? "crosshair" : "default", ...(handMode ? { touchAction: "none" } : {}) }}
      >
        {/* Floor fill */}
        {verts.length >= 3 && (
          <polygon
            points={polyPoints}
            fill="#fff7ed"
            stroke="hsl(16 100% 56%)"
            strokeWidth="1.5"
          />
        )}

        {/* Wall labels */}
        {showDimensions && verts.length >= 2 && verts.map((a, i) => {
          if (floor.shape === "polygon" && i === verts.length - 1) return null;
          const b = verts[(i + 1) % verts.length];
          const sa = toScreen(a.x, a.y);
          const sb = toScreen(b.x, b.y);
          const mx = (sa.sx + sb.sx) / 2;
          const my = (sa.sy + sb.sy) / 2;
          const len = Math.hypot(b.x - a.x, b.y - a.y);
          // Outward normal:
          const dx = b.x - a.x, dy = b.y - a.y;
          const nlen = Math.hypot(dx, dy) || 1;
          const nx = -dy / nlen, ny = dx / nlen;
          const off = 16;
          return (
            <g key={i} pointerEvents="none">
              <text
                x={mx + nx * off}
                y={my + ny * off}
                fill="#475569"
                fontSize="10"
                fontFamily="JetBrains Mono, monospace"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                W{i + 1} · {(len / 1000).toFixed(2)}m
              </text>
            </g>
          );
        })}

        {/* Doors — drawn on the wall edges. Each door replaces a segment of the
             wall with a thicker coloured stroke (kind's colour) and adds a
             swing arc for entry/exit or shutter marks for a loading door. The
             door line is DRAGGABLE — slide it along its wall to adjust offset. */}
        {(floor.doors || []).map((d) => {
          const a = verts[d.edge_idx];
          const b = verts[(d.edge_idx + 1) % verts.length];
          if (!a || !b) return null;
          const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
          const ux = (b.x - a.x) / len, uy = (b.y - a.y) / len;
          const off = Math.max(0, Math.min(len - d.width, d.offset || 0));
          const p1 = { x: a.x + ux * off, y: a.y + uy * off };
          const p2 = { x: a.x + ux * (off + d.width), y: a.y + uy * (off + d.width) };
          const s1 = toScreen(p1.x, p1.y);
          const s2 = toScreen(p2.x, p2.y);
          const meta = DOOR_KINDS[d.kind] || DOOR_KINDS.entry;
          // Inward normal (perpendicular to edge, pointing to polygon centroid).
          const cx = verts.reduce((s, v) => s + v.x, 0) / verts.length;
          const cy = verts.reduce((s, v) => s + v.y, 0) / verts.length;
          let nx = -uy, ny = ux;
          const midX = (p1.x + p2.x) / 2, midY = (p1.y + p2.y) / 2;
          if ((cx - midX) * nx + (cy - midY) * ny < 0) { nx = -nx; ny = -ny; }
          const doorTip = toScreen(p1.x + nx * d.width, p1.y + ny * d.width);
          const swingHinge = s1;
          const labelPt = toScreen(midX + nx * Math.max(d.width * 0.6, 1500), midY + ny * Math.max(d.width * 0.6, 1500));
          const arcR = Math.hypot(doorTip.sx - swingHinge.sx, doorTip.sy - swingHinge.sy);
          const startPointerDown = (e) => {
            if (handMode || measureMode) return;
            e.stopPropagation();
            const svgRect = svgRef.current.getBoundingClientRect();
            // Capture the pointer on the SVG root so subsequent move / up events
            // fire on it even after the pointer leaves the thin door hit-strip.
            try { svgRef.current.setPointerCapture(e.pointerId); } catch (_) { /* older browsers */ }
            setDrag({
              kind: "door",
              id: d.id,
              edge_idx: d.edge_idx,
              startX: e.clientX - svgRect.left,
              startY: e.clientY - svgRect.top,
              origOffset: off,
              doorWidth: d.width,
              wallLen: len,
              ux, uy,
            });
          };
          return (
            <g key={d.id}>
              {/* Wide invisible hit-strip along the door for easier grabbing. */}
              <line
                x1={s1.sx} y1={s1.sy} x2={s2.sx} y2={s2.sy}
                stroke="transparent" strokeWidth="14" strokeLinecap="butt"
                style={{ cursor: "ew-resize", pointerEvents: "stroke" }}
                data-testid={`door-drag-${d.id}`}
                onPointerDown={startPointerDown}
              />
              <line x1={s1.sx} y1={s1.sy} x2={s2.sx} y2={s2.sy}
                stroke={meta.color} strokeWidth="4" strokeLinecap="butt"
                pointerEvents="none" />
              {d.kind === "loading" ? (
                Array.from({ length: 4 }).map((_, i) => {
                  const t = (i + 0.5) / 4;
                  const px = p1.x + (p2.x - p1.x) * t;
                  const py = p1.y + (p2.y - p1.y) * t;
                  const inner = toScreen(px + nx * (d.width * 0.12), py + ny * (d.width * 0.12));
                  const outer = toScreen(px - nx * 200, py - ny * 200);
                  return (
                    <line key={i} x1={outer.sx} y1={outer.sy} x2={inner.sx} y2={inner.sy}
                      stroke={meta.color} strokeWidth="1" strokeDasharray="2 2" pointerEvents="none" />
                  );
                })
              ) : (
                <g pointerEvents="none">
                  <line x1={swingHinge.sx} y1={swingHinge.sy} x2={doorTip.sx} y2={doorTip.sy}
                    stroke={meta.color} strokeWidth="1.4" strokeLinecap="round" />
                  <path
                    d={`M ${s2.sx} ${s2.sy} A ${arcR} ${arcR} 0 0 1 ${doorTip.sx} ${doorTip.sy}`}
                    fill="none" stroke={meta.color} strokeWidth="1" strokeDasharray="3 2"
                  />
                </g>
              )}
              <text x={labelPt.sx} y={labelPt.sy} fill={meta.color} fontSize="9"
                fontFamily="JetBrains Mono, monospace" textAnchor="middle" dominantBaseline="middle"
                pointerEvents="none">
                {meta.label.toUpperCase()}
              </text>
            </g>
          );
        })}

        {/* Persistent AISLE MARKERS — labelled dimension lines between parallel
             rack rows. Distinct from the smart-snap drag-time hints; these stay
             on the canvas whenever `showAisles` is toggled on. Emerald colour. */}
        {showAisles && rowAisles.map((a) => {
            const p1 = toScreen(a.x1, a.y1);
            const p2 = toScreen(a.x2, a.y2);
            const stroke = "hsl(160 84% 39%)"; // emerald
            const label = a.gap >= 1000
              ? `${(a.gap / 1000).toFixed(2)} m`
              : `${a.gap} mm`;
            const labelW = Math.max(70, label.length * 9 + 12);
            if (a.axis === "y") {
              // Horizontal aisle — the gap is measured vertically (top rack row
              // to bottom rack row). Dimension line spans the horizontal overlap.
              const midX = (p1.sx + p2.sx) / 2;
              const midY = (p1.sy + p2.sy) / 2;
              return (
                <g key={a.id} pointerEvents="none">
                  {/* Extension lines (top + bottom of the aisle band) */}
                  <line x1={p1.sx} y1={p1.sy} x2={p2.sx} y2={p1.sy} stroke={stroke} strokeWidth="1" strokeDasharray="2 3" opacity="0.55" />
                  <line x1={p1.sx} y1={p2.sy} x2={p2.sx} y2={p2.sy} stroke={stroke} strokeWidth="1" strokeDasharray="2 3" opacity="0.55" />
                  {/* Vertical dimension line with arrowheads */}
                  <line x1={midX} y1={p1.sy} x2={midX} y2={p2.sy} stroke={stroke} strokeWidth="1.4" />
                  <polygon points={`${midX-5},${p1.sy+7} ${midX+5},${p1.sy+7} ${midX},${p1.sy}`} fill={stroke} />
                  <polygon points={`${midX-5},${p2.sy-7} ${midX+5},${p2.sy-7} ${midX},${p2.sy}`} fill={stroke} />
                  {/* Label pill (offset to the side of the line for readability) */}
                  <rect x={midX + 8} y={midY - 10} width={labelW} height="20" rx="3" fill={stroke} />
                  <text
                    data-testid={`aisle-label-${a.id}`}
                    x={midX + 8 + labelW / 2} y={midY + 1}
                    fill="white" fontSize="11"
                    fontFamily="JetBrains Mono, monospace"
                    fontWeight="700"
                    textAnchor="middle" dominantBaseline="middle"
                  >
                    {label}
                  </text>
                </g>
              );
            }
            // Vertical aisle — gap measured horizontally between two vertical rack columns
            const midX = (p1.sx + p2.sx) / 2;
            const midY = (p1.sy + p2.sy) / 2;
            return (
              <g key={a.id} pointerEvents="none">
                <line x1={p1.sx} y1={p1.sy} x2={p1.sx} y2={p2.sy} stroke={stroke} strokeWidth="1" strokeDasharray="2 3" opacity="0.55" />
                <line x1={p2.sx} y1={p1.sy} x2={p2.sx} y2={p2.sy} stroke={stroke} strokeWidth="1" strokeDasharray="2 3" opacity="0.55" />
                <line x1={p1.sx} y1={midY} x2={p2.sx} y2={midY} stroke={stroke} strokeWidth="1.4" />
                <polygon points={`${p1.sx+7},${midY-5} ${p1.sx+7},${midY+5} ${p1.sx},${midY}`} fill={stroke} />
                <polygon points={`${p2.sx-7},${midY-5} ${p2.sx-7},${midY+5} ${p2.sx},${midY}`} fill={stroke} />
                <rect x={midX - labelW / 2} y={midY - 22} width={labelW} height="20" rx="3" fill={stroke} />
                <text
                  data-testid={`aisle-label-${a.id}`}
                  x={midX} y={midY - 11}
                  fill="white" fontSize="11"
                  fontFamily="JetBrains Mono, monospace"
                  fontWeight="700"
                  textAnchor="middle" dominantBaseline="middle"
                >
                  {label}
                </text>
              </g>
            );
          })}

        {/* Compliance — flagged narrow aisles as red translucent overlays with
             a "gap in mm" label. Only rendered when compliance.enabled. */}
        {compliance.enabled && (() => {
          const narrow = findNarrowAisles(objects, compliance.threshold);
          return narrow.map((f) => {
            const p1 = toScreen(f.x1, f.y1);
            const p2 = toScreen(f.x2, f.y2);
            const w = Math.abs(p2.sx - p1.sx);
            const h = Math.abs(p2.sy - p1.sy);
            const label = `${f.gap}mm`;
            return (
              <g key={f.id} pointerEvents="none">
                <rect
                  x={Math.min(p1.sx, p2.sx)}
                  y={Math.min(p1.sy, p2.sy)}
                  width={w}
                  height={h}
                  fill="hsl(0 84% 60% / 0.22)"
                  stroke="hsl(0 84% 60%)"
                  strokeWidth="1"
                  strokeDasharray="4 3"
                />
                <rect
                  x={Math.min(p1.sx, p2.sx) + w / 2 - Math.max(30, label.length * 4)}
                  y={Math.min(p1.sy, p2.sy) + h / 2 - 8}
                  width={Math.max(60, label.length * 8)}
                  height="16"
                  fill="hsl(0 84% 60%)"
                  rx="2"
                />
                <text
                  x={Math.min(p1.sx, p2.sx) + w / 2}
                  y={Math.min(p1.sy, p2.sy) + h / 2}
                  fill="white"
                  fontSize="10"
                  fontFamily="JetBrains Mono, monospace"
                  fontWeight="700"
                  textAnchor="middle"
                  dominantBaseline="middle"
                >
                  {label}
                </text>
              </g>
            );
          });
        })()}

        {/* Vertex handles */}
        {verts.map((v, i) => {
          const s = toScreen(v.x, v.y);
          return (
            <circle
              key={`v${i}`}
              cx={s.sx}
              cy={s.sy}
              r={5}
              fill="hsl(16 100% 56%)"
              stroke="#ffffff"
              strokeWidth="1.5"
              style={{ cursor: "move" }}
              onPointerDown={(e) => {
                if (handMode) return;
                e.stopPropagation();
                const rect = svgRef.current.getBoundingClientRect();
                setDrag({
                  kind: "vertex",
                  idx: i,
                  startX: e.clientX - rect.left,
                  startY: e.clientY - rect.top,
                });
              }}
            />
          );
        })}

        {/* Obstacles */}
        {(floor.obstacles || []).map((ob) => {
          const s = toScreen(ob.x, ob.y);
          const w = ob.length * view.scale;
          const h = ob.width * view.scale;
          const isSel = ob.id === selectedId;
          return (
            <g key={ob.id}>
              <rect
                x={s.sx} y={s.sy} width={w} height={h}
                fill="#94a3b8"
                stroke={isSel ? "hsl(205 100% 60%)" : "#475569"}
                strokeWidth={isSel ? 2 : 1}
                strokeDasharray={isSel ? "0" : "3 2"}
                style={{ cursor: "move" }}
                data-testid={`obstacle-${ob.id}`}
                onPointerDown={(e) => {
                  if (handMode) return;
                  e.stopPropagation();
                  setSelected(ob.id);
                  const rect = svgRef.current.getBoundingClientRect();
                  setDrag({
                    kind: "obstacle", id: ob.id,
                    startX: e.clientX - rect.left, startY: e.clientY - rect.top,
                    origX: ob.x, origY: ob.y,
                  });
                }}
              />
              <text x={s.sx + w / 2} y={s.sy + h / 2} fill="#0f172a" fontSize="10" fontFamily="JetBrains Mono, monospace" textAnchor="middle" dominantBaseline="middle" pointerEvents="none">
                {ob.label}
              </text>
            </g>
          );
        })}

        {/* Objects */}
        {objects.map((o) => {
          const fp = objectFootprint(o);
          const s = toScreen(o.position.x, o.position.y);
          const wPx = fp.length * view.scale;
          const hPx = fp.width * view.scale;
          const isSel = o.id === selectedId;
          // Top view = rotation around vertical (Y) axis only.
          const rotY = typeof o.rotation === "number" ? o.rotation : (o.rotation?.y || 0);
          return (
            <g key={o.id} transform={`rotate(${rotY} ${s.sx + wPx / 2} ${s.sy + hPx / 2})`}>
              <rect
                x={s.sx} y={s.sy} width={wPx} height={hPx}
                fill={o.color + "33"}
                stroke={isSel ? "hsl(205 100% 60%)" : o.color}
                strokeWidth={isSel ? 2 : 1.25}
                style={{ cursor: "move" }}
                onPointerDown={(e) => {
                  if (handMode) return;
                  e.stopPropagation();
                  setSelected(o.id);
                  if (o.locked) return;
                  const rect = svgRef.current.getBoundingClientRect();
                  setDrag({
                    kind: "object", id: o.id,
                    startX: e.clientX - rect.left, startY: e.clientY - rect.top,
                    origX: o.position.x, origY: o.position.y,
                  });
                }}
              />
              {/* Hatch lines for racks: thin internal beams */}
              {(o.type === "pallet_rack" || o.type === "drive_in_rack") && (() => {
                const bays = o.type === "pallet_rack" ? (o.props.num_bays || 1) : (o.props.lanes || 1);
                const lines = [];
                for (let i = 1; i < bays; i++) {
                  const x = s.sx + (wPx * i) / bays;
                  lines.push(<line key={i} x1={x} y1={s.sy} x2={x} y2={s.sy + hPx} stroke={o.color} strokeWidth="0.75" />);
                }
                return <>{lines}</>;
              })()}
              <text x={s.sx + wPx / 2} y={s.sy + hPx / 2} fill="#0f172a" fontSize="10" fontFamily="JetBrains Mono, monospace" textAnchor="middle" dominantBaseline="middle" pointerEvents="none">
                {OBJECT_DEFS[o.type]?.label}
              </text>
            </g>
          );
        })}

        {/* ---------- Smart-snap guide lines (magenta) + aisle-gap dimension ---------- */}
        {snapHints && (() => {
          const els = [];
          const stroke = "hsl(322 90% 55%)";
          const [vA, vB] = snapHints.vLines || [];
          const [hA, hB] = snapHints.hLines || [];
          for (const x of snapHints.vLines || []) {
            const s = toScreen(x, 0);
            els.push(<line key={`gx-${x}`} x1={s.sx} y1={0} x2={s.sx} y2={size.h} stroke={stroke} strokeWidth="1" strokeDasharray="4 3" pointerEvents="none" />);
          }
          for (const y of snapHints.hLines || []) {
            const s = toScreen(0, y);
            els.push(<line key={`gy-${y}`} x1={0} y1={s.sy} x2={size.w} y2={s.sy} stroke={stroke} strokeWidth="1" strokeDasharray="4 3" pointerEvents="none" />);
          }
          // Aisle dimension readout — shows the gap in mm between the two vertical
          // guide lines (or horizontal). Only when a non-zero gap was snapped.
          if (snapHints.gapX > 0 && vA != null && vB != null) {
            const midY = size.h / 2;
            const s1 = toScreen(vA, 0), s2 = toScreen(vB, 0);
            const label = snapHints.gapX >= 1000 ? `${(snapHints.gapX / 1000).toFixed(2)}m` : `${snapHints.gapX}mm`;
            const lblW = Math.max(50, label.length * 7 + 8);
            els.push(<line key="dimx" x1={s1.sx} y1={midY} x2={s2.sx} y2={midY} stroke={stroke} strokeWidth="1.2" pointerEvents="none" />);
            els.push(<line key="dimx-a" x1={s1.sx} y1={midY-5} x2={s1.sx} y2={midY+5} stroke={stroke} strokeWidth="1.2" />);
            els.push(<line key="dimx-b" x1={s2.sx} y1={midY-5} x2={s2.sx} y2={midY+5} stroke={stroke} strokeWidth="1.2" />);
            els.push(<rect key="dimx-lbl-bg" x={(s1.sx + s2.sx) / 2 - lblW / 2} y={midY - 20} width={lblW} height="14" fill="hsl(322 90% 55%)" rx="2" />);
            els.push(
              <text key="dimx-lbl" x={(s1.sx + s2.sx) / 2} y={midY - 13} fill="white"
                fontSize="10" fontFamily="JetBrains Mono, monospace" fontWeight="600"
                textAnchor="middle" dominantBaseline="middle" pointerEvents="none">
                {label}
              </text>
            );
          }
          if (snapHints.gapY > 0 && hA != null && hB != null) {
            const midX = size.w / 2;
            const s1 = toScreen(0, hA), s2 = toScreen(0, hB);
            const label = snapHints.gapY >= 1000 ? `${(snapHints.gapY / 1000).toFixed(2)}m` : `${snapHints.gapY}mm`;
            const lblW = Math.max(50, label.length * 7 + 8);
            els.push(<line key="dimy" x1={midX} y1={s1.sy} x2={midX} y2={s2.sy} stroke={stroke} strokeWidth="1.2" />);
            els.push(<line key="dimy-a" x1={midX-5} y1={s1.sy} x2={midX+5} y2={s1.sy} stroke={stroke} strokeWidth="1.2" />);
            els.push(<line key="dimy-b" x1={midX-5} y1={s2.sy} x2={midX+5} y2={s2.sy} stroke={stroke} strokeWidth="1.2" />);
            els.push(<rect key="dimy-lbl-bg" x={midX + 6} y={(s1.sy + s2.sy) / 2 - 8} width={lblW} height="14" fill="hsl(322 90% 55%)" rx="2" />);
            els.push(
              <text key="dimy-lbl" x={midX + 6 + lblW / 2} y={(s1.sy + s2.sy) / 2 - 1} fill="white"
                fontSize="10" fontFamily="JetBrains Mono, monospace" fontWeight="600"
                textAnchor="middle" dominantBaseline="middle" pointerEvents="none">
                {label}
              </text>
            );
          }
          return <>{els}</>;
        })()}

        {/* ---------- Measuring tape ---------- */}
        {measure && (() => {
          const a = toScreen(measure.a.x, measure.a.y);
          const end = measure.b || cursorMm;
          if (!end) {
            return (
              <circle cx={a.sx} cy={a.sy} r={4} fill="hsl(205 100% 45%)" stroke="#fff" strokeWidth="1.5" />
            );
          }
          const b = toScreen(end.x, end.y);
          const dx = b.sx - a.sx, dy = b.sy - a.sy;
          const len = Math.hypot(dx, dy) || 1;
          const px = -dy / len * 6, py = dx / len * 6;
          // Distance in mm (real world)
          const dxMm = end.x - measure.a.x;
          const dyMm = end.y - measure.a.y;
          const distMm = Math.hypot(dxMm, dyMm);
          const distLabel = distMm >= 1000
            ? `${(distMm / 1000).toFixed(2)} m`
            : `${Math.round(distMm)} mm`;
          // Label position — perpendicular offset from midpoint (so line stays visible)
          const midSx = (a.sx + b.sx) / 2;
          const midSy = (a.sy + b.sy) / 2;
          const labelOffset = 22;
          const nx = -dy / len, ny = dx / len;
          const labelX = midSx + nx * labelOffset;
          const labelY = midSy + ny * labelOffset;
          const labelW = Math.max(90, distLabel.length * 9 + 16);
          return (
            <g pointerEvents="none">
              <line x1={a.sx} y1={a.sy} x2={b.sx} y2={b.sy}
                stroke="hsl(205 100% 45%)" strokeWidth="1.8"
                strokeDasharray={measure.b ? "0" : "5 3"} />
              <line x1={a.sx + px} y1={a.sy + py} x2={a.sx - px} y2={a.sy - py} stroke="hsl(205 100% 45%)" strokeWidth="1.8" />
              <line x1={b.sx + px} y1={b.sy + py} x2={b.sx - px} y2={b.sy - py} stroke="hsl(205 100% 45%)" strokeWidth="1.8" />
              <circle cx={a.sx} cy={a.sy} r={4} fill="hsl(205 100% 45%)" stroke="#fff" strokeWidth="1.4" />
              <circle cx={b.sx} cy={b.sy} r={4} fill="hsl(205 100% 45%)" stroke="#fff" strokeWidth="1.4" />

              {/* Large prominent live measurement label — always visible while tape is active */}
              <rect
                x={labelX - labelW / 2} y={labelY - 12}
                width={labelW} height="24" rx="4"
                fill="hsl(205 100% 45%)"
                stroke="#fff" strokeWidth="1.4"
              />
              <text
                data-testid="tape-distance"
                x={labelX} y={labelY + 1}
                fill="white"
                fontSize="14"
                fontFamily="JetBrains Mono, monospace"
                fontWeight="700"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {distLabel}
              </text>

              {/* Delta x / delta y readout — smaller, below the main label */}
              <text
                x={labelX} y={labelY + 22}
                fill="hsl(205 100% 35%)"
                fontSize="10"
                fontFamily="JetBrains Mono, monospace"
                fontWeight="600"
                textAnchor="middle"
              >
                Δx {Math.round(Math.abs(dxMm))}mm · Δy {Math.round(Math.abs(dyMm))}mm
              </text>
            </g>
          );
        })()}
      </svg>

      {/* Floating helpers */}
      <div className="absolute top-2 left-2 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-sm px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-600 pointer-events-none shadow-sm">
        2D · Top View · scale 1:{Math.round(1 / view.scale)} · snap {snap}mm
        {cursorMm && (
          <span className="ml-2 text-slate-500">
            · x {(cursorMm.x / 1000).toFixed(2)}m · y {(cursorMm.y / 1000).toFixed(2)}m
          </span>
        )}
      </div>
      {measureMode && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur-sm border border-sky-400 rounded-sm px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-sky-700 shadow-sm">
          {measure ? (measure.b ? "click to start a new measurement · esc to exit" : "click end point · esc to cancel") : "click start point · esc to exit"}
        </div>
      )}
      {floor.shape === "polygon" && verts.length < 3 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-sm border border-orange-300 rounded-sm px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-orange-600 shadow-sm">
          click on canvas to add points · need at least 3
        </div>
      )}
    </div>
  );
}
